import {
  formatPhonePartial,
  getCountryOptions,
  inferCountryFromInternational,
  normalizePhone,
  type CountryOption,
  type NormalizedPhone,
  type PhoneConfig
} from "@quiz/quiz-core";
import { button, el } from "../utils";

export interface PhoneInputCopy {
  search: string;
  aria: string;
  locale?: string;
  placeholder?: string;
}

export class PhoneInput {
  readonly root = el("div", "phone-input");
  private readonly countryButton = button("phone-input__country", "", () => this.toggle());
  private readonly input = el("input", "phone-input__number") as HTMLInputElement;
  private readonly panel = el("div", "phone-input__panel");
  private readonly search = el("input", "phone-input__search") as HTMLInputElement;
  private readonly list = el("div", "phone-input__list");
  private readonly countries: CountryOption[];
  private readonly searchable: boolean;
  private selected: CountryOption;

  constructor(config: PhoneConfig, copy: PhoneInputCopy) {
    const names = copy.locale ? new Intl.DisplayNames([copy.locale], { type: "region" }) : undefined;
    const allCountries = getCountryOptions().map((country) => ({ ...country, name: names?.of(country.iso2) ?? country.name }));
    const preferred = [...new Set([...config.preferredCountries, "US", "CA", "GB"])]
      .map((iso2) => allCountries.find((country) => country.iso2 === iso2))
      .filter(Boolean) as CountryOption[];
    const preferredIds = new Set(preferred.map((country) => country.iso2));
    this.countries = [...preferred, ...allCountries.filter((country) => !preferredIds.has(country.iso2))];
    this.selected = this.countries.find((country) => country.iso2 === config.defaultCountry) ?? this.countries[0];
    this.searchable = config.searchable;

    this.root.append(this.countryButton, this.input, this.panel);
    this.input.type = "tel";
    this.input.inputMode = "tel";
    this.input.autocomplete = "tel";
    this.input.placeholder = copy.placeholder ?? "601 123 456";
    this.input.setAttribute("aria-label", copy.aria);

    this.panel.hidden = true;
    this.search.type = "search";
    this.search.placeholder = copy.search;
    this.search.setAttribute("aria-label", copy.search);
    if (this.searchable) this.panel.append(this.search);
    this.panel.append(this.list);

    this.countryButton.setAttribute("aria-haspopup", "listbox");
    this.countryButton.setAttribute("aria-expanded", "false");
    this.list.setAttribute("role", "listbox");

    this.search.addEventListener("input", () => this.renderList(this.search.value));
    this.search.addEventListener("keydown", (event) => {
      if (event.key === "Escape") this.close();
    });
    this.input.addEventListener("input", () => this.handleInput());
    this.input.addEventListener("paste", () => window.setTimeout(() => this.handleInput(), 0));
    document.addEventListener("click", (event) => {
      if (!this.root.contains(event.target as Node)) this.close();
    });

    this.renderCountry();
  }

  get value(): NormalizedPhone {
    return normalizePhone(this.input.value, this.selected.iso2);
  }

  focus(): void {
    this.input.focus();
  }

  setInvalid(invalid: boolean): void {
    this.root.classList.toggle("is-invalid", invalid);
    this.input.setAttribute("aria-invalid", String(invalid));
  }

  private handleInput(): void {
    const inferredCountry = this.input.value.trim().startsWith("+") ? inferCountryFromInternational(this.input.value) : undefined;
    if (inferredCountry) {
      const country = this.countries.find((candidate) => candidate.iso2 === inferredCountry);
      if (country) {
        this.selected = country;
        this.renderCountry();
      }
    }
    const cursorAtEnd = this.input.selectionStart === this.input.value.length;
    this.input.value = formatPhonePartial(this.input.value, this.selected.iso2);
    if (cursorAtEnd) this.input.setSelectionRange(this.input.value.length, this.input.value.length);
    this.setInvalid(false);
  }

  private toggle(): void {
    if (this.panel.hidden) this.open();
    else this.close();
  }

  private open(): void {
    this.renderList(this.search.value);
    this.panel.hidden = false;
    this.countryButton.setAttribute("aria-expanded", "true");
    if (this.searchable) this.search.focus();
    else this.list.querySelector<HTMLButtonElement>(".phone-input__option")?.focus();
  }

  private close(): void {
    this.panel.hidden = true;
    this.countryButton.setAttribute("aria-expanded", "false");
    this.search.value = "";
  }

  private renderCountry(): void {
    this.countryButton.replaceChildren(
      renderFlag(this.selected.iso2),
      el("span", "phone-input__dial", this.selected.dialCode),
      el("span", "phone-input__chevron", "⌄")
    );
    this.countryButton.setAttribute("aria-label", `${this.selected.name} ${this.selected.dialCode}`);
  }

  private renderList(filter: string): void {
    const value = filter.trim().toLowerCase();
    const matches = this.countries.filter(
      (country) =>
        country.name.toLowerCase().includes(value) ||
        englishCountryNames.get(country.iso2)?.includes(value) ||
        countryAliases[country.iso2]?.some((alias) => alias.includes(value)) ||
        country.iso2.toLowerCase().includes(value) ||
        country.dialCode.includes(value)
    );
    this.list.replaceChildren(
      ...matches.map((country) => {
        const option = button("phone-input__option", "", () => {
          this.selected = country;
          this.renderCountry();
          this.close();
          this.input.focus();
        });
        const label = el("span", "phone-input__option-main");
        label.append(renderFlag(country.iso2, true), el("span", "", country.name));
        option.append(label, el("span", "phone-input__option-code", country.dialCode));
        option.setAttribute("role", "option");
        option.setAttribute("aria-selected", String(country.iso2 === this.selected.iso2));
        return option;
      })
    );
  }
}

const englishCountryNames = new Map(getCountryOptions("en").map(({ iso2, name }) => [iso2, name.toLowerCase()]));
const countryAliases: Record<string, string[]> = {
  US: ["сша", "usa", "united states of america", "штати"],
  CA: ["canada"],
  GB: ["uk", "britain", "great britain", "британія"]
};

function renderFlag(iso2: string, lazy = false): HTMLElement {
  const flag = el("span", "phone-input__flag");
  flag.setAttribute("aria-hidden", "true");
  const image = document.createElement("img");
  image.src = `/assets/flags/${iso2.toLowerCase()}.svg`;
  image.alt = "";
  image.width = 24;
  image.height = 18;
  if (lazy) image.loading = "lazy";
  image.addEventListener("error", () => {
    flag.replaceChildren(el("span", "phone-input__flag-fallback", iso2));
  });
  flag.append(image);
  return flag;
}
